import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const drawerWidth = 420;

export const getDrawerWidth = (open: boolean) => {
  return open ? drawerWidth : 0;
};

interface MapContainerProps {
  open: boolean;
}

export const MapContainer = styled(Box)<MapContainerProps>(({ theme, open }) => ({
  position: 'relative',
  flex: 1,
  width: open ? '100%' : '96.1vw',
  height: 'calc(100vh - 94px)',
  overflow: 'hidden',
  display: 'flex',
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  [theme.breakpoints.down('xl')]: {
    width: open ? '100%' : '95vw',
  },
  [theme.breakpoints.down('xmd')]: {
    width: open ? '100%' : '93vw',
  },
}));

interface StyledMapMaxContentProps {
  paddingBottom?: number;
}

export const shouldForwardProp = (prop: PropertyKey) => prop !== 'paddingBottom';

export const StyledMapMaxContent = styled(Box, {
  shouldForwardProp,
})<StyledMapMaxContentProps>(({ theme, paddingBottom }) => ({
  margin: '0 auto',
  height: 'auto',
  overflowX: paddingBottom === 0 ? 'hidden' : 'auto',
  overflowY: 'hidden',
  maxWidth: '100%',
  paddingBottom: theme.spacing(paddingBottom),

  '> table': {
    minWidth: 900,
  },
}));

interface StyledTableMaxContentProps {
  open: boolean;
}

export const StyledTableMaxContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<StyledTableMaxContentProps>(({ theme, open }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: open ? 'calc(100vw - 520px)' : '100%',
  padding: theme.spacing(0, 3),
  overflow: 'auto',
  height: 'calc(100vh - 180px)',

  [theme.breakpoints.down('xxl')]: {
    maxWidth: open ? '1600px' : '100%',
    height: 'calc(100vh - 180px)',
  },
  [theme.breakpoints.down('xl')]: {
    maxWidth: open ? 'calc(100vw - 520px)' : '100%',
  },
  [theme.breakpoints.down('lg')]: {
    maxWidth: open ? 'calc(100vw - 500px)' : '100%',
  },
  [theme.breakpoints.down('xmd')]: {
    maxWidth: open ? 'calc(100vw - 480px)' : '100%',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    padding: theme.spacing(0, 2),
    height: 'calc(100vh - 260px)',
  },
}));

const BaseLoading = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  maxWidth: '200px',
  height: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '999px',
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.common.white,
  [theme.breakpoints.down('md')]: {
    bottom: theme.spacing(12),
  },
}));

export const MapLoading = styled(BaseLoading)(() => ({
  bottom: 24,
  right: 24,
}));

export const ControlContainer = styled(Box)(() => ({
  position: 'absolute',
  top: 24,
  right: 24,
}));

export const MapSearchPanel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 24,
  left: 24,
  zIndex: 1000,
  width: 430,
  maxWidth: 'calc(100% - 48px)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const SelectedMunicipalitiesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

export const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  flexGrow: 1,
  width: open ? '100%' : '95vw',
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100vh - 94px)`,
  overflow: 'hidden',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  [theme.breakpoints.down('xl')]: {
    width: open ? '100%' : '93.5vw',
  },
  [theme.breakpoints.down('xmd')]: {
    width: open ? '100%' : '90.5vw',
  },
}));

export const StyledList = styled(Box)(({ theme, open }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  maxHeight: '300px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
  overflowY: 'auto',
  zIndex: 1000,
  ...(open && {
    display: 'block',
  }),
  ...(!open && {
    display: 'none',
  }),
}));

export const StyledListItem = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '8px',
  height: '40px',
  backgroundColor: theme.palette.common.white,
  zIndex: 1000,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const MapCardContainer = styled('div')(() => ({
  position: 'absolute',
  top: '25%',
  left: '60%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1000,
  maxWidth: '360px',
}));

export const MapCardArrow = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '12px solid transparent',
  borderRight: '12px solid transparent',
  borderTop: `12px solid ${theme.palette.common.white}`,
  zIndex: 1001,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-13px',
    left: '-13px',
    width: 0,
    height: 0,
    borderLeft: '13px solid transparent',
    borderRight: '13px solid transparent',
    borderTop: `13px solid ${theme.palette.common.white}`,
    zIndex: -1,
  },
}));

export const MapCardBackdrop = styled('div')(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999,
}));

export const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2.9),
  backgroundColor: theme.palette.common.white,
}));

export const InputContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  boxSizing: 'border-box',
  minWidth: '430px',
  maxWidth: '100%',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));
